import { z } from "zod";

import type { ReturnTo } from "@/types/dragon-tiles";

import { AnalogSequencingStore } from "@/features/DragonTiles/AnalogSequencing/store";
import { isConnectorTileReturn } from "@/features/DragonTiles/returnToUtils";
import { ParentTileViewStore } from "@/features/DragonTiles/store/parentTileView";

export type TileSelectReturnMedium = "email" | "sms";

export type ConnectorTileSelectReturnSearch = {
  selectedEventId?: string;
  selectedAudienceId?: string;
  openTileSelect?: boolean;
  tileSelectMedium?: TileSelectReturnMedium;
  tileSelectInsertAt?: number;
};

export type ConnectorReturnSearch = ConnectorTileSelectReturnSearch & {
  connectorParentTileId?: string;
};

export type ConnectorTileSelectReturnInput = Omit<
  Partial<ConnectorTileSelectReturnSearch>,
  "selectedEventId" | "selectedAudienceId"
> & {
  selectedEventId?: string | null;
  selectedAudienceId?: string | null;
  medium?: TileSelectReturnMedium;
  insertAtIndex?: number;
};

export type ConnectorReturnSearchInput = ConnectorTileSelectReturnInput & {
  returnTo?: ReturnTo;
  connectorParentTileId?: string;
};

export const connectorReturnSearchSchema = z.object({
  selectedEventId: z.string().optional(),
  selectedAudienceId: z.string().optional(),
  openTileSelect: z.boolean().optional(),
  tileSelectMedium: z.enum(["email", "sms"]).optional(),
  tileSelectInsertAt: z
    .union([z.string(), z.number()])
    .transform((v) => (typeof v === "string" ? Number(v) : v))
    .pipe(z.number().int().nonnegative())
    .optional(),
  connectorParentTileId: z.string().optional(),
});

function buildTileSelectReturnSearch(
  input: ConnectorTileSelectReturnInput,
): ConnectorTileSelectReturnSearch {
  if (
    typeof input.selectedEventId === "string" &&
    (input.medium === "email" || input.medium === "sms") &&
    input.openTileSelect !== true
  ) {
    return {
      selectedEventId: input.selectedEventId,
      ...(typeof input.selectedAudienceId === "string"
        ? { selectedAudienceId: input.selectedAudienceId }
        : {}),
      openTileSelect: true,
      tileSelectMedium: input.medium,
      ...(input.insertAtIndex !== undefined
        ? { tileSelectInsertAt: input.insertAtIndex }
        : {}),
    };
  }

  const result: ConnectorTileSelectReturnSearch = {};

  if (typeof input.selectedEventId === "string") {
    result.selectedEventId = input.selectedEventId;
  }
  if (typeof input.selectedAudienceId === "string") {
    result.selectedAudienceId = input.selectedAudienceId;
  }
  if (input.openTileSelect === true) {
    result.openTileSelect = true;
  }
  if (input.tileSelectMedium === "email" || input.tileSelectMedium === "sms") {
    result.tileSelectMedium = input.tileSelectMedium;
  }

  const insertAt = input.tileSelectInsertAt ?? input.insertAtIndex;
  if (typeof insertAt === "number" && !Number.isNaN(insertAt)) {
    result.tileSelectInsertAt = insertAt;
  }

  return result;
}

/** Build connector drawer return params for create/edit navigation and navigate back. */
export function connectorReturnSearch(
  input: ConnectorReturnSearchInput,
): ConnectorReturnSearch {
  return {
    ...buildTileSelectReturnSearch(input),
    ...(isConnectorTileReturn(input.returnTo) && input.connectorParentTileId
      ? { connectorParentTileId: input.connectorParentTileId }
      : {}),
  };
}

/** Merge active tile-select state when returning to the connector drawer mid-pick. */
export function connectorReturnSearchWithActiveTileSelect(
  input: ConnectorReturnSearchInput,
): ConnectorReturnSearch {
  const { tileSelect, connectorEventId } =
    AnalogSequencingStore.testing.getState();
  const selectedAudienceId =
    ParentTileViewStore.testing.getState().selectedAudienceId;
  const shouldRestoreTileSelect =
    isConnectorTileReturn(input.returnTo) &&
    tileSelect.open &&
    typeof connectorEventId === "string" &&
    (tileSelect.medium === "email" || tileSelect.medium === "sms");

  return connectorReturnSearch(
    shouldRestoreTileSelect
      ? {
          ...input,
          selectedEventId: connectorEventId,
          selectedAudienceId: selectedAudienceId ?? undefined,
          medium: tileSelect.medium,
          insertAtIndex: tileSelect.insertAtIndex,
        }
      : input,
  );
}
